"use client";

import React, { useMemo, useRef } from "react";
import Button from "../button";
import Spinner from "../spinner/loading";
import type {
  AsaasDocumentRequirement,
  UploadBillingDocumentPayload,
} from "@/types";

interface BillingDocumentsListProps {
  documents: AsaasDocumentRequirement[];
  loading: boolean;
  uploading: boolean;
  onUpload: (payload: UploadBillingDocumentPayload) => Promise<void>;
  onRefresh: () => Promise<void>;
  rejectReasons?: Record<string, unknown> | null;
}

const formatStatus = (status?: string): { label: string; tone: string } => {
  if (!status) {
    return { label: "Sem status", tone: "bg-gray-100 text-gray-600" };
  }

  const normalized = status.toLowerCase();

  if (normalized.includes("reject") || normalized.includes("denied")) {
    return { label: status, tone: "bg-red-100 text-red-700" };
  }
  if (normalized.includes("approved") || normalized.includes("accept")) {
    return { label: status, tone: "bg-green-100 text-green-700" };
  }
  if (normalized.includes("pending") || normalized.includes("waiting")) {
    return { label: status, tone: "bg-amber-100 text-amber-700" };
  }

  return { label: status, tone: "bg-blue-100 text-blue-700" };
};

const documentKey = (doc: AsaasDocumentRequirement, index: number) =>
  `${doc.id ?? doc.documentId ?? index}`;

interface DocumentCardProps {
  document: AsaasDocumentRequirement;
  uploading: boolean;
  onUpload: (payload: UploadBillingDocumentPayload) => Promise<void>;
}

const DocumentCard: React.FC<DocumentCardProps> = ({
  document,
  uploading,
  onUpload,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const status = formatStatus(document.status);
  const files = Array.isArray(document.files) ? document.files : [];
  const onboardingUrl = document.onboardingUrl;
  const onboardingUrlExpirationDate = document.onboardingUrlExpirationDate;

  const handleOpenFileDialog = () => {
    inputRef.current?.click();
  };

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = async (
    event,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await onUpload({
        documentId: document.documentId ?? document.id ?? "unknown",
        file,
      });
    } finally {
      // Reset input to allow reselecting the same file if needed
      event.target.value = "";
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <h4 className="text-sm font-semibold text-gray-800">
            {document.name ||
              document.label ||
              `Documento ${documentKey(document, 0)}`}
          </h4>
          {document.description && (
            <p className="text-xs text-gray-600">{document.description}</p>
          )}
          {document.rejectionReason && (
            <p className="mt-1 text-xs text-red-600">
              Motivo da rejeição: {document.rejectionReason}
            </p>
          )}
          {onboardingUrl && (
            <div className="mt-2 text-xs">
              <a
                href={onboardingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-blue-600 underline hover:text-blue-700"
              >
                Acessar link de onboarding
              </a>
              {onboardingUrlExpirationDate && (
                <span className="ml-2 text-[11px] text-gray-500">
                  Expira em {onboardingUrlExpirationDate}
                </span>
              )}
            </div>
          )}
        </div>
        <span
          className={`inline-flex h-7 items-center rounded-full px-3 text-xs font-medium ${status.tone}`}
        >
          {status.label}
        </span>
      </div>

      {files.length > 0 && (
        <ul className="mt-4 space-y-2 text-xs text-gray-600">
          {files.map((file, index) => {
            const fileStatus = formatStatus(file.status as string | undefined);
            return (
              <li
                key={`${documentKey(document, index)}-file-${file.id ?? index}`}
                className="flex flex-col rounded border border-gray-100 bg-gray-50 p-2 md:flex-row md:items-center md:justify-between"
              >
                <div className="flex flex-col">
                  <span className="font-medium text-gray-700">
                    {file.name ||
                      file.filename ||
                      file.id ||
                      `Arquivo ${index + 1}`}
                  </span>
                  {(file.uploaded_at || file.updated_at) && (
                    <span className="text-[11px] text-gray-500">
                      Atualizado em {file.updated_at || file.uploaded_at}
                    </span>
                  )}
                </div>
                <span
                  className={`mt-2 inline-flex h-6 items-center rounded-full px-2 text-[11px] font-semibold md:mt-0 ${fileStatus.tone}`}
                >
                  {fileStatus.label}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <div className="hidden mt-4  flex-col gap-2 border-t border-dashed border-gray-200 pt-4 md:flex-row md:items-center md:justify-between">
        <div className="text-xs text-gray-500">
          Envie um arquivo válido para atualizar este documento.
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept="image/*,application/pdf"
            onChange={handleFileChange}
          />
          <Button
            type="button"
            variant="outline"
            disabled={uploading}
            onClick={handleOpenFileDialog}
            text={uploading ? "Enviando..." : "Enviar arquivo"}
          />
        </div>
      </div>
    </div>
  );
};

export const BillingDocumentsList: React.FC<BillingDocumentsListProps> = ({
  documents,
  loading,
  uploading,
  onUpload,
  onRefresh,
  rejectReasons,
}) => {
  const hasDocuments = documents.length > 0;

  const rejectReasonsText = useMemo(() => {
    if (!rejectReasons) {
      return "";
    }

    if (typeof rejectReasons === "string") {
      return rejectReasons;
    }

    try {
      return JSON.stringify(rejectReasons, null, 2);
    } catch (error) {
      return String(rejectReasons);
    }
  }, [rejectReasons]);

  return (
    <section className="space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
      <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Documentos obrigatórios
          </h3>
          <p className="text-xs text-slate-600">
            Faça o upload dos documentos solicitados para liberar a análise da
            conta.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => void onRefresh()}
          disabled={loading}
          text={loading ? "Atualizando..." : "Recarregar"}
        />
      </header>

      {rejectReasonsText && (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <strong className="block font-semibold">Motivos de rejeição</strong>
          <pre className="mt-1 whitespace-pre-wrap break-words text-[11px]">
            {rejectReasonsText}
          </pre>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-6">
          <Spinner />
        </div>
      )}

      {!loading && !hasDocuments && (
        <div className="rounded border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          Nenhum documento pendente. Aguarde a atualização da análise.
        </div>
      )}

      {!loading &&
        hasDocuments &&
        documents.map((document, index) => (
          <DocumentCard
            key={documentKey(document, index)}
            document={document}
            uploading={uploading}
            onUpload={onUpload}
          />
        ))}
    </section>
  );
};

export default BillingDocumentsList;
